/** A point or a direction, in mm in the part's import frame. */
export type SharpEdgeVec3 = [number, number, number];

/** One stretch of an edge sharper than the threshold. */
export interface SharpEdge {
  type: 'concave' | 'convex';
  /** Sharpest angle along the stretch, degrees: air side if concave, material side if convex. */
  minAngleDeg: number;
  /** Polyline along the edge. */
  points: SharpEdgeVec3[];
  /** Outward normals of the two faces at each point. */
  n1: SharpEdgeVec3[];
  n2: SharpEdgeVec3[];
}

/** The sharp edges of one part, as measured at import. */
export interface PartSharpEdges {
  thresholdDeg: number;
  edges: SharpEdge[];
}

/**
 * The part's entry in a parts/<id>.sharpedges.json file, the report brepsmith's
 * sharpedges workflow writes cut down to that part. Null for anything else.
 */
export function parsePartSharpEdges(raw: unknown, partId: string): PartSharpEdges | null {
  if (!raw || typeof raw !== 'object') return null;
  const file = raw as { thresholdDeg?: unknown; parts?: unknown };
  if (!Array.isArray(file.parts)) return null;
  const part = file.parts.find((p) => p && typeof p === 'object' && String((p as { id?: unknown }).id) === partId)
    ?? (file.parts.length === 1 ? file.parts[0] : null);
  const edges = (part as { edges?: unknown } | null)?.edges;
  if (!Array.isArray(edges)) return null;
  return {
    thresholdDeg: Number(file.thresholdDeg) || 0,
    edges: edges.filter(
      (e): e is SharpEdge => !!e && Array.isArray((e as SharpEdge).points) && (e as SharpEdge).points.length >= 2
    ),
  };
}
